import { Listbox } from '@headlessui/react';
import { ChangeEvent, useMemo, useState } from 'react';
import ChevronDownIcon from '../../icons/ChevronDown';
import MinimumSalarySlider from '../MinimumSalarySlider';
import { removeNonNumericCharacters } from 'wwg-utilities';
import { SingleFilter } from '../../../lib/types';
import AgeFilterOptions from '../AgeFilterOptions';
import StandardFilterOptions from '../StandardFilterOptions';
import useFilter from '../../../lib/hooks/filters';
import RequirementsFilterOptions from '../RequirementsFilterOptions';
import { useRegion } from '../../../lib/providers/regionProvider';
import FilterLocationComponent from '../LocationFinder/FilterLocationComponent';

interface DesktopFilterProps {
  paramLabel: string;
  label: string;
  filterOptions: string[];
  potentialNumberOfResults: number;
  isLoading?: boolean;
  updatePotentialFilterForParam: (filter: SingleFilter) => void;
  seeAllResults: () => void;
  defaultValue?: string[]
}

const DesktopFilter: React.FC<DesktopFilterProps> = ({
  paramLabel,
  label,
  filterOptions,
  potentialNumberOfResults,
  updatePotentialFilterForParam,
  seeAllResults,
  defaultValue
}) => {

  const { userLocationInfo } = useRegion();


  // This stores data of which filters are currently toggled on by user
  const [selectedFilters, setSelectedFilters] = useState(defaultValue || []);


  const { clearSelectedFilters, onChange } = useFilter(
    selectedFilters,
    paramLabel,
    setSelectedFilters,
    updatePotentialFilterForParam,
  );

  // Needed for non standard filter options like Age
  // and MinimumSalarySlider
  const manualUpdate = (value: string) => {
    setSelectedFilters([value]);
  };

  const onClick = () => {
    seeAllResults();
  };



  const labelValue = useMemo(() => {

    if (paramLabel == 'distance' && (!userLocationInfo?.zipcode || !selectedFilters[0])) {
      return 'Filter by distance'
    } else if (paramLabel == 'distance') {
      return `Distance: ${selectedFilters[0]}`
    }
    return label;
  }, [selectedFilters])

  return (
    <div className="flex-1">
      <Listbox
        onChange={onChange}
        className="relative"
        value={selectedFilters}
        multiple
        as="div"
      >
        <Listbox.Button data-testid={`filter-${paramLabel}`} className="items-center justify-between hidden min-w-full p-2 md:flex filter">
          <p className="whitespace-nowrap">{labelValue}</p>
          <ChevronDownIcon className="w-4 h-4 ml-2 stroke-inherit" />
        </Listbox.Button>
        <div className="hidden md:block">
          <Listbox.Options
            className={`${paramLabel} ${paramLabel == 'salary' ? '-left-[275px]' : '-left-[50px]'}  filter-options absolute top-10  min-w-full cursor-pointer bg-white z-[19] shadow-filter py-2 rounded-lg pb-[5rem] outline-none`}
          >
            <div className="overflow-y-scroll max-h-80 ml-1.5">
              {paramLabel == 'requirements' ? (
                <RequirementsFilterOptions
                  filterOptions={filterOptions}
                  paramLabel={paramLabel}
                />
              ) : paramLabel == 'salary' ? (
                <MinimumSalarySlider
                  onChange={manualUpdate}
                  options={filterOptions}
                  setSelectedFilters={setSelectedFilters}
                  selectedFilters={selectedFilters}
                />
              ) : paramLabel == 'age' ? (
                <AgeFilterOptions
                  value={selectedFilters.join('')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const val =
                      removeNonNumericCharacters(
                        (event.target as HTMLInputElement).value,
                      )?.toString() || '';

                    if (val != 'NaN') {
                      setSelectedFilters([val]);
                    } else {
                      setSelectedFilters(['']);
                    }
                  }}
                />
              ) :
                (
                  Boolean(paramLabel == 'distance' && !userLocationInfo?.zipcode) ?
                    <FilterLocationComponent />
                    :
                    <StandardFilterOptions
                      filterOptions={filterOptions}
                      paramLabel={paramLabel}
                      singleSelect={paramLabel == 'distance'}
                    />
                )}
            </div>

            <div className="absolute bottom-0 left-0 right-0 grid px-6 py-4 m-0 text-center bg-white shadow-2xl grid-cols-filter">
              <button
                className="font-bold text-button-primary font-base"
                onClick={clearSelectedFilters}
                tabIndex={1}
              >
                Clear
              </button>
              <Listbox.Button
                data-testid={`see_results_${paramLabel}`}
                as="button"
                tabIndex={1}
                className="bg-button-primary w-full lg:w-[175px] ml-auto py-3 font-base font-bold rounded-full text-white"
                onClick={onClick}
              >
                See {potentialNumberOfResults || 0} Results
              </Listbox.Button>
            </div>
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
};

export default DesktopFilter;
